import {Icon} from '@atoms';
import {useBreakPoint, useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {
  ConnectionUser,
  getAllConnectedUsers,
  getIsLoadingConnections,
  useAppSelector,
  useHomeBuildingId,
} from '@lib/store';
import {Avatar} from '@molecules';
import {FlexCol, H3, H4, Subtitle} from '@quarks';
import {cn} from '@utils';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {
  constructConnectionsOverview,
  useConnectionsSchedule,
} from '../../bookWorkspace/BookingDetailsCard/timeslots/useConnectionsSchedule';
import {ConnectionLocation, ConnectionOnMapModal} from './ConnectionOnMapModal';
import {NoConnections, NoOneThere} from './EmptyState';
import {ConnectionsLoader} from './ConnectionsLoader';

export const ConnectionOverview_timeslots = (props: {dateKey: string; asBigPage?: boolean; hideTitle?: boolean}) => {
  const bp = useBreakPoint();
  const {t} = useTranslation();
  const {setBigPages, openModal, addPages} = useModal();

  const showConnectionOnMap = (user: ConnectionUser, location: ConnectionLocation) => {
    const mapView = (
      <ConnectionOnMapModal
        user={user}
        workspace={location}
      />
    );

    trackEvent('Home_ConnectionsOverview__ExpandMap');

    addPages([mapView]);
    if (props.asBigPage) setBigPages([mapView]);
    openModal();
  };

  const connectionsSchedule = useConnectionsSchedule(props.dateKey);
  const buildingId = useHomeBuildingId(props.dateKey);
  const connectionsLoading = useAppSelector(getIsLoadingConnections);
  const connectedUsers = useAppSelector(getAllConnectedUsers);
  const overview = constructConnectionsOverview(connectedUsers, connectionsSchedule.workspaceResevations, {buildingId});

  if (connectionsLoading || connectionsSchedule.status === 'pending') return <ConnectionsLoader />;

  return (
    <FlexCol
      gap={16}
      className="w-full">
      {!props.hideTitle && <H3 className="pl-0">{t('screen:AllConnections')}</H3>}

      {connectedUsers.length === 0 && <NoConnections />}
      {overview.length === 0 && <NoOneThere />}
      {overview.map((o) => (
        <Fragment key={o.locationName}>
          <H4 className="pl-0">{o.locationName}</H4>
          <div className={cn('grid full-w gap-6 grid-cols-1', {'grid-cols-2': bp === 'large'})}>
            {o.occupants.map(({user, reservations}) => {
              // TODO: this shows the first reservation of the day
              const reservation = reservations[0];
              const {workspace} = reservation;
              const workspaceLabel =
                'areaName' in workspace
                  ? `${workspace.floorName}, ${workspace.areaName}`
                  : 'floorName' in workspace
                  ? workspace.floorName
                  : '';

              return (
                <div
                  className="flex w-full gap-2 items-center"
                  key={user.id}>
                  <Avatar
                    className="flex-none"
                    shouldShowCheckIn
                    user={user}
                  />
                  <div className="overflow-hidden mr-auto">
                    <p className="truncate">{user.name}</p>
                    {workspaceLabel && <Subtitle className="truncate">{workspaceLabel}</Subtitle>}
                  </div>
                  {workspace ? (
                    <button
                      className="flex-none"
                      aria-label={t('ShowOnMap')}
                      onClick={() => {
                        if ('areaName' in workspace) {
                          showConnectionOnMap(user, workspace);
                        }
                      }}>
                      <Icon
                        icon="map"
                        size="20px"
                        color="#767676"
                      />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Fragment>
      ))}
    </FlexCol>
  );
};
